import {
  AlertDialog,
  AlertDialogClose,
  Button,
  Chip,
  EmptyState,
  Icon,
  Menu,
  Skeleton,
} from '@starter/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { useBrands } from '../brands/api.ts';
import { EntityCard, EntityGrid } from '../brands/entity-card.tsx';
import { authClient } from '../lib/auth.ts';
import {
  type ProductStatus,
  useArchiveAnyProduct,
  useCreateProduct,
  useProducts,
  useRestoreAnyProduct,
} from '../products/api.ts';
import { CardMedia } from '../products/card-media.tsx';
import { ProductCreateDialog } from '../products/create-dialog.tsx';

// catalog-guard: allow brand_color_roles — `primary`, `secondary` and `text`
// here are Button variants and a Skeleton shape. Colour roles are read from the
// catalog on the brand's page, not restated here.

/**
 * "My products" — the same index archetype as "My brands", with one more
 * filter. Three of the product's six destinations are this page with a
 * different noun, which is most of the argument for having archetypes at all.
 */
function Products() {
  const { status, brandId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const products = useProducts(status, brandId);
  const brands = useBrands('active');
  const createProduct = useCreateProduct();
  const archive = useArchiveAnyProduct();
  const restore = useRestoreAnyProduct();
  const [creating, setCreating] = useState(false);
  /** The card whose archive is being confirmed, and the one just archived. */
  const [confirming, setConfirming] = useState<{ id: string; name: string } | undefined>();
  const [undo, setUndo] = useState<{ id: string; name: string } | undefined>();

  const rows = products.data?.products ?? [];
  /**
   * Which emptiness this is. The product has two and they are not the same
   * sentence: an account that never had a product is invited to start, and one
   * that archived its last is told what a product is for. Telling them apart
   * needs to know whether anything is in the archive, which is one extra
   * request and only on an empty screen.
   */
  const archived = useProducts('archived');
  const everHad = (archived.data?.products.length ?? 0) > 0;

  const setSearch = (next: { status?: ProductStatus; brandId?: string }) =>
    navigate({ search: { status, brandId, ...next } });

  const brandItems = (brands.data?.brands ?? []).map((one) => ({
    label: one.displayName,
    value: one.id,
  }));

  return (
    <div className="flex flex-col">
      <header className="flex flex-wrap items-start gap-4 py-2">
        <h1
          className="min-w-0 flex-1 font-display text-[28px] leading-[34px] font-bold tracking-[-0.7px] text-ink"
          data-testid="products-title"
        >
          My products
        </h1>
        <Button
          data-testid="product-create"
          disabled={brandItems.length === 0}
          onClick={() => setCreating(true)}
          variant="primary"
        >
          <Icon name="plus" size={18} />
          New product
        </Button>
      </header>

      {/*
        Measured: an empty list carries no filter row at all — not a disabled
        one, not an empty one. The row is about narrowing something, and there
        is nothing to narrow.

        OURS, and the line where copying stops: the original hides the row even
        when the archive has something in it, which leaves the archive with no
        door at all — the filter is the only way to it. So the row goes when
        there is nothing anywhere, and stays the moment there is something to
        go back to. `prd/DIVERGENCE.md` carries the reason.
      */}
      {rows.length === 0 && !everHad ? null : (
        <div className="flex flex-wrap items-center gap-2 py-4">
          <div className="ml-auto flex items-center gap-2">
            {brandId ? (
              <Chip
                data-testid="product-brand-filter"
                onClick={() => setSearch({ brandId: undefined })}
                selected
              >
                {brandItems.find((item) => item.value === brandId)?.label ?? 'One brand'}
                <Icon name="x" size={14} />
              </Chip>
            ) : null}
            <Chip
              data-testid="product-archive-toggle"
              onClick={() => setSearch({ status: status === 'archived' ? 'active' : 'archived' })}
              selected={status === 'archived'}
            >
              {status === 'archived' ? 'Archived' : 'Active'}
            </Chip>
          </div>
        </div>
      )}

      {products.isPending ? (
        <EntityGrid>
          {[0, 1, 2, 3].map((slot) => (
            <div
              className="overflow-hidden rounded-card border-2 border-border-subtle bg-surface-raised"
              key={slot}
            >
              <Skeleton className="aspect-square w-full rounded-none" />
              {/* Three lines, because the card has three: a skeleton that
                  promises two drops the grid by a row when the data lands. */}
              <div className="flex flex-col gap-1.5 p-4">
                <Skeleton className="w-1/3" shape="text" />
                <Skeleton className="w-4/5" shape="text" />
                <Skeleton className="w-1/2" shape="text" />
              </div>
            </div>
          ))}
        </EntityGrid>
      ) : rows.length > 0 ? (
        <EntityGrid testId="product-grid">
          {rows.map((product) => (
            <EntityCard
              /*
               * The badge is the product's own, and it goes the moment a
               * photograph lands — which is what lets the plate underneath be
               * the name alone, with no icon and no dimming.
               */
              badge={
                product.photoCount === 0 ? (
                  <span className="inline-flex items-center rounded-card border border-border-subtle bg-surface-raised px-3 py-1.5 text-[16px] leading-4 text-ink">
                    No photo
                  </span>
                ) : undefined
              }
              key={product.id}
              media={<CardMedia name={product.name} uploadId={product.heroUploadId} />}
              /*
               * The overline counts what has been made from this product. Zero
               * is the interesting case, and the line below says what it means
               * rather than leaving it to be worked out from a number. It
               * counted nothing but zero until A4 gave the account something
               * that could make one.
               */
              overline={`${product.materialCount ?? 0} ${product.materialCount === 1 ? 'material' : 'materials'}`}
              params={{ productId: product.id }}
              revealActions
              actions={
                <Menu.Root>
                  <Menu.Trigger
                    aria-label={`Actions for ${product.name}`}
                    className="inline-flex size-10 items-center justify-center rounded-[12px] bg-black/50 text-white transition-colors hover:bg-black/65"
                    data-testid="product-card-actions"
                  >
                    <Icon name="ellipsis" size={24} />
                  </Menu.Trigger>
                  <Menu.Portal>
                    <Menu.Positioner align="end">
                      {/* One item, measured. Not «edit», not «add a photo». */}
                      <Menu.Popup>
                        <Menu.Item
                          data-testid="product-card-archive"
                          onClick={() => setConfirming({ id: product.id, name: product.name })}
                        >
                          Archive this product
                        </Menu.Item>
                      </Menu.Popup>
                    </Menu.Positioner>
                  </Menu.Portal>
                </Menu.Root>
              }
              status={
                (product.materialCount ?? 0) === 0
                  ? 'Never advertised'
                  : (product.brandName ?? undefined)
              }
              testId="product-card"
              title={product.name}
              to="/products/$productId"
            />
          ))}
        </EntityGrid>
      ) : (
        <EmptyState
          actions={
            brandItems.length === 0 ? (
              <Button render={<a href="/brands">Create a brand first</a>} variant="primary" />
            ) : status === 'archived' ? (
              <Button onClick={() => setSearch({ status: 'active' })} variant="secondary">
                Show active
              </Button>
            ) : (
              /*
                Secondary here while the header's is primary: two «New
                product»s on one screen and only one of them black — the
                emptiness offers, the header commands. Measured.
              */
              <Button
                data-testid="product-create-empty"
                onClick={() => setCreating(true)}
                variant="secondary"
              >
                <Icon name="plus" size={16} />
                New product
              </Button>
            )
          }
          data-testid="products-empty"
          /*
           * Two emptinesses, and the product does not use one sentence for
           * both: an account that never had a product is invited to start, and
           * one that archived its last is reminded what a product is *for*.
           * Measured on 2026-09-21, both of them.
           */
          description={
            brandItems.length === 0
              ? 'A product belongs to a brand, and there are no brands yet.'
              : status === 'archived'
                ? 'Nothing has been archived yet.'
                : everHad
                  ? 'Products give an advert something to show.'
                  : 'Add your first product — its photographs are where every advert starts.'
          }
          icon={<Icon name={status === 'archived' ? 'search' : 'box'} />}
          title={
            status === 'archived'
              ? 'No archived products'
              : everHad
                ? 'Add your first product'
                : 'No products yet'
          }
          variant="inline"
        />
      )}

      {/*
        A dialog, and a first step that asks what is being sold. It was a card
        that unfolded here with two fields in it — which the data model had
        already outgrown: `sellable_kinds` has carried three kinds since the
        first migration and nothing ever asked (`prd/DIVERGENCE.md`).
      */}
      <ProductCreateDialog
        brands={brandItems}
        defaultBrandId={brandId}
        error={createProduct.error?.message}
        onCreate={async ({ brandId: chosen, name: chosenName, sellableKind }) => {
          const created = await createProduct.mutateAsync({
            brandId: chosen,
            name: chosenName,
            sellableKind,
          });
          setCreating(false);
          await navigate({
            params: { productId: created.product.id },
            to: '/products/$productId',
          });
        }}
        onOpenChange={setCreating}
        open={creating}
        pending={createProduct.isPending}
      />

      {/*
        Archiving asks, and then leaves a way back for a few seconds. Both are
        measured: the confirmation is 480 wide with the product's own name in
        the question and the only danger-red button in the interface, and the
        snackbar is the only place an archive can be undone without going to
        find the archive.
      */}
      <AlertDialog
        description="It leaves the shelf and keeps everything it has. You can restore it later."
        onOpenChange={(open: boolean) => {
          if (!open) setConfirming(undefined);
        }}
        open={confirming !== undefined}
        title={`Archive ${confirming?.name ?? 'this product'}?`}
      >
        <div className="flex justify-end gap-3">
          <AlertDialogClose render={<Button variant="secondary">Cancel</Button>} />
          <Button
            data-testid="product-archive-confirm"
            disabled={archive.isPending}
            onClick={() => {
              const chosen = confirming;
              if (!chosen) return;
              archive.mutate(chosen.id, { onSuccess: () => setUndo(chosen) });
              setConfirming(undefined);
            }}
            variant="danger"
          >
            Archive
          </Button>
        </div>
      </AlertDialog>

      {undo ? (
        <div
          className="fixed right-4 bottom-8 z-50 flex w-[380px] max-w-[calc(100%-2rem)] items-center gap-3 rounded-card border border-border-subtle bg-surface-raised py-4 pr-3 pl-4 shadow-card"
          data-testid="products-snackbar"
          role="status"
        >
          <span className="flex-1 text-[16px] leading-[22.4px] text-ink">Product archived</span>
          <Button
            data-testid="products-snackbar-undo"
            disabled={restore.isPending}
            onClick={() => {
              restore.mutate(undo.id);
              setUndo(undefined);
            }}
            variant="secondary"
          >
            Undo
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/products/')({
  validateSearch: z.object({
    status: z.enum(['active', 'archived']).default('active'),
    brandId: z.uuid().optional(),
  }),
  beforeLoad: async ({ location }) => {
    const { data } = await authClient.getSession();
    if (!data) throw redirect({ to: '/login', search: { redirectTo: location.href } });
  },
  component: Products,
});
